import { zodResolver } from '@hookform/resolvers/zod'
import { useEffect, useRef, useState } from 'react'
import { useForm } from 'react-hook-form'
import { useNavigate } from 'react-router-dom'
import { toast } from 'sonner'
import { z } from 'zod'

import { Button } from '@/components/ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { ApiError } from '@/services/api/client'
import { createWorkspace as createWorkspaceApi } from '@/services/api/workspace.service'
import { useAuthStore } from '@/stores/auth.store'
import { usePermissionStore } from '@/stores/permission.store'
import { useWorkspaceStore } from '@/stores/workspace.store'



const workspaceSchema = z.object({
  workspaceName: z.string().min(1, 'Workspace name is required'),
})

type WorkspaceFormData = z.infer<typeof workspaceSchema>

export function RegisterWorkspacePage() {
  const navigate = useNavigate()
  const { userId, loginToken, setAccessToken, setLoginToken, workspaces, setWorkspaces } = useAuthStore()
  const { setWorkspace } = useWorkspaceStore()
  const permissionStore = usePermissionStore()
  const [isLoading, setIsLoading] = useState(false)
  // Set once the workspace exists so clearing the login token doesn't bounce the user to /login.
  const completed = useRef(false)

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<WorkspaceFormData>({
    resolver: zodResolver(workspaceSchema),
    defaultValues: { workspaceName: '' },
  })

  useEffect(() => {
    // The login token is memory-only: after a page reload it is gone and the user must sign in again.
    if (!completed.current && (!userId || !loginToken)) {
      navigate('/login', { replace: true })
    }
  }, [userId, loginToken, navigate])

  const onSubmit = async (data: WorkspaceFormData) => {
    if (!userId || !loginToken) return
    setIsLoading(true)
    try {
      const workspaceResponse = await createWorkspaceApi({
        name: data.workspaceName,
        ownerId: userId,
        loginToken,
      })

      completed.current = true
      setAccessToken(workspaceResponse.accessToken)
      setLoginToken(null)

      const newId = workspaceResponse.id
      setWorkspace(newId, workspaceResponse.name)
      setWorkspaces([...(workspaces ?? []), { id: newId, name: workspaceResponse.name }])

      // Hydrate permission store so the UI immediately reflects the new owner's access
      permissionStore.setIsSuperUser(workspaceResponse.superUser)
      permissionStore.setIsDeveloper(workspaceResponse.developerRole)

      navigate('/')
    } catch (e) {
      if (e instanceof ApiError && e.status === 401) {
        toast.error('Your sign-in has expired. Please sign in again.')
        navigate('/login', { replace: true })
      } else {
        toast.error('Workspace creation failed', {
          description: 'Please check your information and try again.',
        })
      }
    } finally {
      setIsLoading(false)
    }
  }

  if (!userId || !loginToken) return null

  return (
    <div className="flex min-h-screen items-center justify-center bg-neutral-50 px-4">
      <div className="w-full max-w-md">
        <Card>
          <CardHeader className="space-y-1">
            <CardTitle className="text-2xl font-bold">Create a workspace</CardTitle>
            <CardDescription>Give your workspace a name</CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="workspaceName">Workspace Name</Label>
                <Input
                  id="workspaceName"
                  placeholder="My Company"
                  {...register('workspaceName')}
                  aria-invalid={!!errors.workspaceName}
                />
                {errors.workspaceName && (
                  <p className="text-sm text-destructive-600">{errors.workspaceName.message}</p>
                )}
              </div>

              <Button type="submit" className="w-full" disabled={isLoading}>
                {isLoading ? 'Creating workspace...' : 'Create workspace'}
              </Button>
            </form>
          </CardContent>
        </Card>
      </div>
    </div>
  )
}
